import { useRef, useState } from 'react';
import { Link } from 'react-router';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { IMPORT_COLUMNS, IMPORT_KINDS, IMPORT_KIND_LABELS, IMPORT_LIMITS, IMPORT_STATUS_LABELS, type ImportKind, type ImportResult, type ImportRowStatus } from '@kontor/shared';
import { api, ApiError } from '../../api';
import { Stepper, Tip } from './Stepper';

const STEPS = ['Auswählen', 'Vorbereiten', 'Datei laden', 'Prüfen', 'Bestätigen', 'Fertig'];
const errorText = (e: unknown, fallback: string) => (e instanceof ApiError ? (e.status === 422 ? (Object.values(e.fields)[0] ?? e.message) : e.message) : fallback);
const STATUS_TONE: Record<ImportRowStatus, string> = { create: 'imp-badge--ok', update: 'imp-badge--info', skip: 'imp-badge--off', error: 'imp-badge--bad' };
const KIND_INFO: Record<ImportKind, { text: string; to: string; open: string }> = {
  contacts: { text: 'Kunden, Lieferanten und andere Kontakte mit Anschrift, E-Mail und Telefon.', to: '/kontakte', open: 'Kontakte öffnen' },
  products: { text: 'Waren und Dienstleistungen mit Artikelnummer, Preis und Steuersatz.', to: '/produkte', open: 'Produkte öffnen' },
};

/** Excel speichert „CSV“ meist in Windows-1252, andere Programme in UTF-8: erst UTF-8 versuchen, sonst Windows-1252 */
async function readText(file: File): Promise<string> {
  const bytes = await file.arrayBuffer();
  try {
    return new TextDecoder('utf-8', { fatal: true }).decode(bytes).replace(/^﻿/, '');
  } catch {
    return new TextDecoder('windows-1252').decode(bytes);
  }
}

/** Zeilen mit Fehler oder Übersprungenes als CSV zum Nacharbeiten, ohne weiteren Aufruf des Servers */
function downloadReport(kind: ImportKind, result: ImportResult) {
  const rows = result.rows.filter((r) => r.status === 'error' || r.status === 'skip');
  const quote = (v: string) => `"${v.replace(/"/g, '""')}"`;
  const text = ['Zeile;Stand;Name;Hinweis', ...rows.map((r) => [r.line, IMPORT_STATUS_LABELS[r.status], quote(r.label), quote(r.message ?? '')].join(';'))].join('\r\n');
  const url = URL.createObjectURL(new Blob([`﻿${text}`], { type: 'text/csv;charset=utf-8' }));
  const a = document.createElement('a');
  a.href = url;
  a.download = `Importbericht_${IMPORT_KIND_LABELS[kind]}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

/**
 * Import als geführter Ablauf (KON-171): Auswählen, Vorbereiten, Datei laden, Prüfen, Bestätigen, Fertig. Jeder Schritt sagt, was zu tun ist und worauf es ankommt. Gelesen wird die
 * Datei erst im Schritt „Prüfen“, angelegt wird erst nach „Bestätigen“. Zurück geht jederzeit, solange noch nichts angelegt ist.
 */
export function ImportWizard({ onClose }: { onClose: () => void }) {
  const client = useQueryClient();
  const input = useRef<HTMLInputElement>(null);
  const [step, setStep] = useState(0);
  const [kind, setKind] = useState<ImportKind>('contacts');
  const [duplicates, setDuplicates] = useState<'skip' | 'update'>('skip');
  const [file, setFile] = useState<{ name: string; text: string; size: number } | null>(null);
  const [result, setResult] = useState<ImportResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [dragging, setDragging] = useState(false);

  const run = useMutation({
    mutationFn: async (dryRun: boolean) => (await api<{ data: ImportResult }>('POST', `/imports/${kind}`, { csv: file!.text, duplicates, dryRun })).data,
    onSuccess: (data) => {
      setResult(data);
      setError(null);
      if (data.dryRun) setStep(3);
      else {
        setStep(5);
        for (const key of ['contacts', 'products', 'marketing-audience', 'chart-options']) client.invalidateQueries({ queryKey: [key] });
      }
    },
    onError: (e, dryRun) => {
      setError(errorText(e, 'Die Datei konnte nicht verarbeitet werden.'));
      // Eine abgelehnte Datei gehört in den Schritt, in dem man sie tauscht
      if (dryRun) setStep(2);
    },
  });

  const choose = async (f: File | undefined) => {
    if (!f) return;
    setResult(null);
    setError(null);
    if (f.size > IMPORT_LIMITS.maxBytes) {
      setFile(null);
      setError('Die Datei ist zu groß (höchstens 5 MB). Teilen Sie sie in mehrere Dateien auf.');
      return;
    }
    setFile({ name: f.name, text: await readText(f), size: f.size });
  };
  const back = () => {
    setError(null);
    if (step === 3) setResult(null);
    setStep((s) => Math.max(0, s - 1));
  };
  const columns = IMPORT_COLUMNS[kind];
  const counts = result?.counts;
  const writable = counts ? counts.create + counts.update : 0;
  const info = KIND_INFO[kind];

  return (
    <section className="card wiz" aria-labelledby="wiz-title">
      <div className="wiz-head">
        <h2 id="wiz-title">Daten importieren</h2>
        {step < 5 && <button type="button" className="link-button" onClick={onClose}>Abbrechen</button>}
      </div>
      <Stepper steps={STEPS} current={step} />
      {error && <div className="alert alert--error" role="alert">{error}</div>}

      {step === 0 && (
        <div className="wiz-body">
          <h3>Was möchten Sie importieren?</h3>
          <div className="wiz-choices" role="radiogroup" aria-label="Art der Daten">
            {IMPORT_KINDS.map((k) => (
              <button key={k} type="button" role="radio" aria-checked={kind === k} className={`wiz-choice${kind === k ? ' is-on' : ''}`} onClick={() => { setKind(k); setFile(null); setResult(null); }}>
                <strong>{IMPORT_KIND_LABELS[k]}</strong>
                <span>{KIND_INFO[k].text}</span>
              </button>
            ))}
          </div>
          <Tip title="So läuft es ab">Sie laden eine Tabelle (CSV-Datei) hoch, zum Beispiel aus Excel. Wirkia prüft sie zuerst und zeigt Ihnen, was passieren würde. <strong>Angelegt wird erst, wenn Sie ausdrücklich bestätigen.</strong> Bis dahin ändert sich nichts.</Tip>
          <div className="wiz-nav"><span /><button type="button" className="btn btn--primary" onClick={() => setStep(1)}>Weiter</button></div>
        </div>
      )}

      {step === 1 && (
        <div className="wiz-body">
          <h3>Datei vorbereiten</h3>
          <p>Am sichersten starten Sie mit der Vorlage: Sie enthält die richtigen Überschriften und eine Beispielzeile. Tragen Sie Ihre {IMPORT_KIND_LABELS[kind]} darunter ein und löschen Sie die Beispielzeile.</p>
          <div className="wiz-actions"><a className="btn btn--secondary" href={`/api/v1/imports/template/${kind}`}>Vorlage herunterladen (CSV)</a></div>
          <details className="imp-columns" open>
            <summary>Diese Spalten kennt Wirkia</summary>
            <div className="table-card">
              <table className="table table--cards">
                <thead><tr><th>Spalte</th><th>Pflicht</th><th>Bedeutung</th><th>Beispiel</th></tr></thead>
                <tbody>
                  {columns.map((c) => (
                    <tr key={c.key}>
                      <td data-label="Spalte"><strong>{c.label}</strong></td>
                      <td data-label="Pflicht">{c.required ? 'ja' : 'nein'}</td>
                      <td data-label="Bedeutung">{c.hint}</td>
                      <td data-label="Beispiel" className="muted">{c.example}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </details>
          <Tip title="Gut zu wissen">
            <ul>
              <li>Die erste Zeile muss die Überschriften enthalten. Wirkia erkennt sie auch in anderer Schreibweise, zum Beispiel „Strasse“ statt „Straße“.</li>
              <li>Fremde Spalten stören nicht, sie werden ignoriert.</li>
              <li>Aus Excel speichern Sie mit <em>Speichern unter → CSV (Trennzeichen-getrennt)</em> oder <em>CSV UTF-8</em>.</li>
              <li>{kind === 'contacts' ? 'Beziehungsarten (zum Beispiel „Kunde“) müssen vorher in der Einrichtung unter „Kontakte“ angelegt sein.' : 'Preise schreiben Sie in Euro ohne Steuer, mit Komma, zum Beispiel 12,50.'}</li>
              <li>Höchstens {IMPORT_LIMITS.maxRows.toLocaleString('de-DE')} Zeilen je Datei. Größere Listen teilen Sie auf.</li>
            </ul>
          </Tip>
          <div className="wiz-nav"><button type="button" className="btn btn--secondary" onClick={back}>Zurück</button><button type="button" className="btn btn--primary" onClick={() => setStep(2)}>Meine Datei ist fertig</button></div>
        </div>
      )}

      {step === 2 && (
        <div className="wiz-body">
          <h3>Datei laden</h3>
          <div
            className={`wiz-drop${dragging ? ' is-over' : ''}${file ? ' has-file' : ''}`}
            onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
            onDragLeave={() => setDragging(false)}
            onDrop={(e) => { e.preventDefault(); setDragging(false); void choose(e.dataTransfer.files[0]); }}
          >
            {file ? (
              <>
                <strong className="imp-file">{file.name}</strong>
                <span className="muted small">{(file.size / 1024).toLocaleString('de-DE', { maximumFractionDigits: 1 })} KB gelesen</span>
              </>
            ) : (
              <span>CSV-Datei hierher ziehen oder auswählen</span>
            )}
            <button type="button" className="btn btn--secondary" onClick={() => input.current?.click()}>{file ? 'Andere Datei wählen' : 'Datei auswählen'}</button>
            <input ref={input} type="file" hidden accept=".csv,.txt,text/csv" onChange={(e) => { void choose(e.target.files?.[0]); e.target.value = ''; }} />
          </div>

          <fieldset className="imp-choice">
            <legend>Was soll mit Einträgen geschehen, die es in Wirkia schon gibt?</legend>
            <label><input type="radio" name="dup" checked={duplicates === 'skip'} onChange={() => setDuplicates('skip')} /> <span><strong>Überspringen</strong><span className="muted small"> – vorhandene Einträge bleiben, wie sie sind (empfohlen)</span></span></label>
            <label><input type="radio" name="dup" checked={duplicates === 'update'} onChange={() => setDuplicates('update')} /> <span><strong>Aktualisieren</strong><span className="muted small"> – gefüllte Zellen überschreiben die vorhandenen Angaben, leere Zellen lassen sie stehen</span></span></label>
          </fieldset>
          <Tip title="Woran erkennt Wirkia einen vorhandenen Eintrag?">{kind === 'contacts' ? 'Kontakte gelten als gleich, wenn die E-Mail-Adresse übereinstimmt, ohne E-Mail der Name mit der Postleitzahl.' : 'Produkte gelten als gleich, wenn die Artikelnummer übereinstimmt, ohne Artikelnummer die Bezeichnung.'} Kommt dieselbe Person in Ihrer Datei zweimal vor, wird nur die erste Zeile verarbeitet.</Tip>
          <div className="wiz-nav">
            <button type="button" className="btn btn--secondary" onClick={back}>Zurück</button>
            <button type="button" className="btn btn--primary" disabled={!file || run.isPending} onClick={() => run.mutate(true)}>{run.isPending ? 'Wird geprüft …' : 'Datei prüfen'}</button>
          </div>
        </div>
      )}

      {step === 3 && result && counts && (
        <div className="wiz-body" aria-live="polite">
          <h3>Prüfergebnis</h3>
          <p className="muted">Es wurde noch nichts angelegt. Das ist nur die Vorschau zu „{file?.name}“ ({result.total.toLocaleString('de-DE')} Zeilen).</p>
          <div className="imp-counts">
            <div><strong>{counts.create}</strong><span>werden angelegt</span></div>
            {(counts.update > 0 || duplicates === 'update') && <div><strong>{counts.update}</strong><span>werden aktualisiert</span></div>}
            <div><strong>{counts.skip}</strong><span>werden übersprungen</span></div>
            <div className={counts.error > 0 ? 'imp-counts__bad' : ''}><strong>{counts.error}</strong><span>haben einen Fehler</span></div>
          </div>
          {result.ignored.length > 0 && <Tip>Diese Spalten kennt Wirkia nicht und ignoriert sie: <strong>{result.ignored.join(', ')}</strong>.</Tip>}
          {counts.error > 0 ? (
            <Tip tone="warn" title="Zeilen mit Fehler werden nicht importiert">
              Sie haben zwei Möglichkeiten: Korrigieren Sie die Datei und laden Sie sie noch einmal, oder importieren Sie die {writable.toLocaleString('de-DE')} fehlerfreien Zeilen jetzt und die übrigen später. Der Hinweis in der Spalte „Hinweis“ sagt jeweils, was zu ändern ist.
            </Tip>
          ) : (
            <Tip>Alle Zeilen sind in Ordnung.</Tip>
          )}
          {result.rows.length > 0 && (
            <div className="table-card">
              <table className="table table--cards">
                <thead><tr><th>Zeile</th><th>Stand</th><th>{kind === 'contacts' ? 'Name' : 'Bezeichnung'}</th><th>Hinweis</th></tr></thead>
                <tbody>
                  {result.rows.map((r) => (
                    <tr key={r.line}>
                      <td data-label="Zeile">{r.line}</td>
                      <td data-label="Stand"><span className={`imp-badge ${STATUS_TONE[r.status]}`}>{IMPORT_STATUS_LABELS[r.status]}</span></td>
                      <td data-label="Name">{r.label}</td>
                      <td data-label="Hinweis">{r.message ?? <span className="muted">–</span>}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {result.truncated && <p className="muted small">Angezeigt sind die ersten 300 Zeilen, Fehler zuerst.</p>}
          <div className="wiz-nav">
            <span className="wiz-actions">
              <button type="button" className="btn btn--secondary" onClick={back}>Andere Datei oder Einstellung</button>
              {(counts.error > 0 || counts.skip > 0) && <button type="button" className="btn btn--secondary" onClick={() => downloadReport(kind, result)}>Fehlerliste herunterladen</button>}
            </span>
            <button type="button" className="btn btn--primary" disabled={writable === 0} onClick={() => setStep(4)}>{writable === 0 ? 'Nichts zu importieren' : 'Weiter'}</button>
          </div>
        </div>
      )}

      {step === 4 && result && counts && (
        <div className="wiz-body">
          <h3>Importieren bestätigen</h3>
          <p>
            Wirkia legt jetzt <strong>{counts.create.toLocaleString('de-DE')} {IMPORT_KIND_LABELS[kind]}</strong> an
            {counts.update > 0 && <> und aktualisiert <strong>{counts.update.toLocaleString('de-DE')}</strong></>}.
            {counts.skip > 0 && <> {counts.skip.toLocaleString('de-DE')} Zeilen werden übersprungen.</>}
            {counts.error > 0 && <> {counts.error.toLocaleString('de-DE')} Zeilen mit Fehler bleiben außen vor.</>}
          </p>
          <Tip tone="warn" title="Das lässt sich nicht mit einem Klick rückgängig machen">
            Angelegte Einträge löschen Sie einzeln in der Liste. Jeder Eintrag steht im Änderungsprotokoll. {duplicates === 'update' && counts.update > 0 ? 'Aktualisierte Einträge behalten ihre alten Werte nur im Protokoll.' : ''}
          </Tip>
          <div className="wiz-nav">
            <button type="button" className="btn btn--secondary" onClick={back} disabled={run.isPending}>Zurück</button>
            <button type="button" className="btn btn--primary" disabled={run.isPending} onClick={() => run.mutate(false)}>{run.isPending ? 'Wird importiert …' : `${writable.toLocaleString('de-DE')} Zeilen jetzt importieren`}</button>
          </div>
        </div>
      )}

      {step === 5 && result && counts && (
        <div className="wiz-body" aria-live="polite">
          <h3>Import abgeschlossen</h3>
          <div className="imp-counts">
            <div><strong>{counts.create}</strong><span>angelegt</span></div>
            {counts.update > 0 && <div><strong>{counts.update}</strong><span>aktualisiert</span></div>}
            <div><strong>{counts.skip}</strong><span>übersprungen</span></div>
            <div className={counts.error > 0 ? 'imp-counts__bad' : ''}><strong>{counts.error}</strong><span>mit Fehler</span></div>
          </div>
          {counts.error > 0 && <Tip tone="warn" title="Einige Zeilen fehlen noch">{counts.error.toLocaleString('de-DE')} Zeilen konnten nicht übernommen werden. Laden Sie die Fehlerliste herunter, korrigieren Sie diese Zeilen in Ihrer Datei und importieren Sie sie noch einmal. Schon vorhandene Einträge werden dabei übersprungen.</Tip>}
          <div className="wiz-nav">
            <span className="wiz-actions">
              <Link className="btn btn--primary" to={info.to}>{info.open}</Link>
              {(counts.error > 0 || counts.skip > 0) && <button type="button" className="btn btn--secondary" onClick={() => downloadReport(kind, result)}>Fehlerliste herunterladen</button>}
            </span>
            <span className="wiz-actions">
              <button type="button" className="btn btn--secondary" onClick={() => { setStep(0); setFile(null); setResult(null); }}>Weitere Datei importieren</button>
              <button type="button" className="btn btn--secondary" onClick={onClose}>Zur Übersicht</button>
            </span>
          </div>
        </div>
      )}
    </section>
  );
}
